import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ReactNode } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import CaretUpIcon from '@flipster/react/icon/generated/CaretUpIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';

import { marketListBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol-info/market-list/state';

type SortKey = 'turnover24h' | 'leverage' | 'displayPrice' | 'priceChangePct24h';

const columns: { key: SortKey; label: ReactNode }[] = [
  { key: 'turnover24h', label: <FormattedMessage id='Autogen_common.r251126.s_24h_volume' /> },
  { key: 'leverage', label: <FormattedMessage id='Assets.r250401.Leverage' /> },
  { key: 'displayPrice', label: <FormattedMessage id='Autogen_common.r251126.s_Price' /> },
  { key: 'priceChangePct24h', label: <FormattedMessage id='Autogen_common.r251126.s_Change' /> },
];

export const ListHeader = () => {
  const { sortAtom, currentTabAtom } = useBunja(marketListBunja);
  const sort = useAtomValue(sortAtom);
  const setSort = useSetAtom(sortAtom);
  const currentTab = useAtomValue(currentTabAtom);

  const hideLeverage = currentTab === 'spot' || currentTab === 'favorites';
  const visibleColumns = hideLeverage ? columns.filter((col) => col.key !== 'leverage') : columns;

  const handleSort = (key: SortKey) => {
    if (sort?.key !== key) {
      setSort({ key, order: 'desc' });
    } else if (sort.order === 'desc') {
      setSort({ key, order: 'asc' });
    } else {
      setSort(undefined);
    }
  };

  const getAriaSort = (key: SortKey): 'ascending' | 'descending' | 'none' => {
    if (sort?.key !== key) return 'none';
    return sort.order === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <div role='row' className={hstack({ gap: 16, px: 16, py: 4 })}>
      {visibleColumns.map((col) => {
        const isSorted = sort?.key === col.key;
        return (
          <div role='columnheader' aria-sort={getAriaSort(col.key)} key={col.key}>
            <button
              type='button'
              className={css(
                hstack.raw({
                  gap: 4,
                  color: 'content.default.level-2',
                  textStyle: 'Text/S/Regular',
                  whiteSpace: 'nowrap',
                }),
                isSorted && { color: 'content.default.level-5' },
              )}
              onClick={() => handleSort(col.key)}
            >
              {col.label}
              <SortIndicator isSorted={isSorted} order={sort?.order} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const SortIndicator = ({ isSorted, order }: { isSorted: boolean; order: 'asc' | 'desc' | undefined }) => (
  <div className={vstack()}>
    <CaretUpIcon
      size={8}
      className={css({
        mt: 2,
        color: isSorted && order === 'asc' ? 'content.default.level-5' : 'content.default.level-1',
      })}
    />
    <CaretDownIcon
      size={8}
      className={css({
        mt: -2,
        color: isSorted && order === 'desc' ? 'content.default.level-5' : 'content.default.level-1',
      })}
    />
  </div>
);
